// src/pages/ChatBot/configs/config-modals.tsx
//
// The two confirmation dialogs the config panel raises:
//   RebuildConfirmModal  — shown before saving any rebuild-class change, so an
//                          admin sees that documents will be re-processed.
//   UnsavedChangesModal  — the leave guard, shown when navigating away from a
//                          dirty panel. Also imported by ManageChat, which owns
//                          the top-tab switch guard (ConfigsPanel can't see it).
// Both ride the house modal shell defined in ConfigsPanel.css (imported here so
// the styles are present regardless of which parent mounts the modal), use real
// <button>s, and close on Escape via the shared useEscapeClose hook. Escape maps
// to the *least destructive* action (Go back / Keep editing).

import { useEscapeClose } from '../../../hooks/useEscapeClose';
import './ConfigsPanel.css';

interface RebuildConfirmModalProps {
  open: boolean;
  // Labels of the rebuild-class settings the pending save touches.
  affectedLabels: string[];
  isSaving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function RebuildConfirmModal({
  open,
  affectedLabels,
  isSaving,
  onConfirm,
  onCancel,
}: RebuildConfirmModalProps) {
  // Escape backs out (the save stays pending). Suspend the handler while the
  // request is in flight so the dialog can't vanish underneath it.
  useEscapeClose(onCancel, open && !isSaving);
  if (!open) return null;

  return (
    <div className="cfg-modal-overlay" role="presentation">
      <div
        className="cfg-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cfg-rebuild-title"
      >
        <div className="cfg-modal-header">
          <h2 id="cfg-rebuild-title" className="cfg-modal-title">
            Re-process documents?
          </h2>
        </div>
        <div className="cfg-modal-body">
          <p className="cfg-modal-text">
            Saving queues a re-process of all uploaded documents in this
            workspace. While it runs, every query keeps being answered from the
            current document processing, completely unaffected — and the switch
            to the new processing is instantaneous the moment the re-process
            completes.
          </p>
          {affectedLabels.length > 0 && (
            <ul className="cfg-modal-list">
              {affectedLabels.map((label) => (
                <li key={label}>{label}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="cfg-modal-footer">
          <button
            type="button"
            className="cfg-btn cfg-btn-ghost"
            onClick={onCancel}
            disabled={isSaving}
          >
            Go back
          </button>
          <button
            type="button"
            className="cfg-btn cfg-btn-primary"
            onClick={onConfirm}
            disabled={isSaving}
          >
            {isSaving ? 'Saving…' : 'Queue rebuild & save'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface UnsavedChangesModalProps {
  open: boolean;
  isSaving: boolean;
  onSave: () => void;
  onDiscard: () => void;
  onKeepEditing: () => void;
}

export function UnsavedChangesModal({
  open,
  isSaving,
  onSave,
  onDiscard,
  onKeepEditing,
}: UnsavedChangesModalProps) {
  // Escape = Keep editing (never discards work). Suspended mid-save.
  useEscapeClose(onKeepEditing, open && !isSaving);
  if (!open) return null;

  return (
    <div className="cfg-modal-overlay" role="presentation">
      <div
        className="cfg-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cfg-unsaved-title"
      >
        <div className="cfg-modal-header">
          <h2 id="cfg-unsaved-title" className="cfg-modal-title">
            Unsaved changes
          </h2>
        </div>
        <div className="cfg-modal-body">
          <p className="cfg-modal-text">You have unsaved config changes.</p>
        </div>
        <div className="cfg-modal-footer cfg-modal-footer-spread">
          <button
            type="button"
            className="cfg-btn cfg-btn-danger-ghost"
            onClick={onDiscard}
            disabled={isSaving}
          >
            Discard changes
          </button>
          <div className="cfg-modal-footer-side">
            <button
              type="button"
              className="cfg-btn cfg-btn-ghost"
              onClick={onKeepEditing}
              disabled={isSaving}
            >
              Keep editing
            </button>
            <button
              type="button"
              className="cfg-btn cfg-btn-primary"
              onClick={onSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
